import * as React from 'react'
import styled from 'styled-components'
import { DUMB_COMPONENT_TYPES, FILTER_TYPE, FORM_ELEMENT_TYPES, FlowCall, NewButton, GroupTable, FlowFormElement, SvgIcon } from 'prace-common-components'
import Grid from '@material-ui/core/Grid'
import { $ButtonContainer, $ModalContainer, $Paragraph } from 'components/FormModal/styles'

const $WrappingTable = styled.div`
  .rdg-cell {
    white-space: normal !important;
    overflow-x: hidden !important;
    word-break: break-word;
  }
`

interface PreviewItemsModalProps {
  call: FlowCall,
  previewIds: number[],
  onSave: (previewIds: number[]) => void,
}

const stepFormColumns = [
  {
    key: 'step',
    type: FILTER_TYPE.NONE,
    name: '',
    noSort: true,
    maxWidth: 180,
  },
  {
    key: 'form',
    type: FILTER_TYPE.NONE,
    name: '',
    noSort: true,
    maxWidth: 180,
  },
]

export const PreviewItemsModal: React.FC<PreviewItemsModalProps> = ({ call, previewIds, onSave }) => {
  const allRows = React.useMemo(() => {
    const result: {
      id: number,
      step: string,
      form: string,
      name: string,
      _isGroupHeader?: boolean,
      _groupId?: number,
      _parentGroupId?: number,
    }[] = []

    const truncate = (s: string, max: number) => s.length > max ? s.slice(0, max - 3) + '...' : s

    ;(call.flow || []).forEach((step) => {
      const stepTitle = truncate(step.title, 20)
      ;(step.flow || []).forEach((form) => {
        const formTitle = truncate(form.title, 20)

        ;((form.flow || []) as FlowFormElement[]).forEach((el) => {
          if (el.elementType === FORM_ELEMENT_TYPES.GROUP) {
            result.push({
              id: -el.id,
              step: stepTitle,
              form: formTitle,
              name: truncate(el.title, 20),
              _isGroupHeader: true,
              _groupId: el.id,
            })
            ;(el.flow || []).forEach((child) => {
              if (DUMB_COMPONENT_TYPES.includes(child.elementType as FORM_ELEMENT_TYPES)) return
              if (child.config?.previewIds?.length) return
              result.push({
                id: child.id,
                step: stepTitle,
                form: formTitle,
                name: truncate(child.title, 44),
                _parentGroupId: el.id,
              })
            })
            return
          }
          if (DUMB_COMPONENT_TYPES.includes(el.elementType as FORM_ELEMENT_TYPES)) return
          if (el.config?.previewIds?.length) return
          result.push({
            id: el.id,
            step: stepTitle,
            form: formTitle,
            name: truncate(el.title, 44),
          })
        })
      })
    })

    return result
  }, [call.flow])

  const groupChildrenMap = React.useMemo(() => {
    const map = new Map<number, number[]>()
    allRows.forEach((row) => {
      if (row._parentGroupId !== undefined) {
        const arr = map.get(row._parentGroupId) ?? []
        arr.push(row.id)
        map.set(row._parentGroupId, arr)
      }
    })
    return map
  }, [allRows])

  const [selectedRows, setSelectedRows] = React.useState<Set<number>>(() => {
    const initial = new Set((previewIds || []).map(Number))
    groupChildrenMap.forEach((children, groupId) => {
      if (children.length > 0 && children.every((c) => initial.has(c))) initial.add(-groupId)
    })
    return initial
  })

  const [collapsedGroupIds, setCollapsedGroupIds] = React.useState<Set<number>>(() => {
    const selectedSet = new Set((previewIds || []).map(Number))
    const collapsed = new Set<number>()
    groupChildrenMap.forEach((children, groupId) => {
      if (!children.some((id) => selectedSet.has(id))) collapsed.add(groupId)
    })
    return collapsed
  })

  const [expandedGroupIds, setExpandedGroupIds] = React.useState<ReadonlySet<unknown>>(() => {
    const ids = new Set<unknown>()
    const selectedSet = new Set((previewIds || []).map(Number))
    allRows.forEach((row) => {
      if (row.id > 0 && selectedSet.has(row.id)) {
        ids.add(row.step)
        ids.add(`${row.step}__${row.form}`)
      }
    })
    return ids
  })

  const visibleRows = React.useMemo(
    () => allRows.filter((row) => row._parentGroupId === undefined || !collapsedGroupIds.has(row._parentGroupId)),
    [allRows, collapsedGroupIds],
  )

  const handleSetSelectedRows = (newSelected: Set<number>) => {
    const result = new Set([...newSelected].filter((id) => id >= 0))
    // Group header just checked (negative id appeared in newSelected)
    for (const id of newSelected) {
      if (id >= 0 || selectedRows.has(id)) continue
      const children = groupChildrenMap.get(-id) ?? []
      children.forEach((c) => result.add(c))
    }
    // Group header just unchecked (negative id was in selectedRows, gone from newSelected)
    for (const id of selectedRows) {
      if (id >= 0 || newSelected.has(id)) continue
      const children = groupChildrenMap.get(-id) ?? []
      children.forEach((c) => result.delete(c))
    }
    // Sync header state to match children
    groupChildrenMap.forEach((children, groupId) => {
      if (children.length > 0 && children.every((c) => result.has(c))) result.add(-groupId)
      else result.delete(-groupId)
    })
    setSelectedRows(result)
  }

  const columns = React.useMemo(() => [
    ...stepFormColumns,
    {
      key: 'name',
      type: FILTER_TYPE.NONE,
      name: '',
      noSort: true,
      formatter: ({ row }: { row: any }) => {
        if (row._isGroupHeader) {
          const isCollapsed = collapsedGroupIds.has(row._groupId as number)
          return (
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontWeight: 600 }}
              onClick={(e) => {
                e.stopPropagation()
                setCollapsedGroupIds((prev) => {
                  const next = new Set(prev)
                  if (next.has(row._groupId as number)) next.delete(row._groupId as number)
                  else next.add(row._groupId as number)
                  return next
                })
              }}
            >
              <SvgIcon name={isCollapsed ? 'chevron-down' : 'chevron-up'} size={16} />
              {row.name}
            </div>
          )
        }
        if (row._parentGroupId !== undefined) {
          return <div style={{ paddingLeft: 20 }}>{row.name}</div>
        }
        return <div>{row.name}</div>
      },
    },
  ], [collapsedGroupIds])

  return (
    <$ModalContainer width={750}>
      <Grid container direction='column' spacing={2}>
        <Grid item>
          <$Paragraph>Select the elements you want to preview:</$Paragraph>
        </Grid>
        <Grid item>
          <$WrappingTable>
            <GroupTable
              showEmptyImg={false}
              rowsPerPage={10}
              noFilters
              noHeader
              rows={visibleRows}
              columns={columns}
              selectedRows={selectedRows}
              groupBy={['step', 'form']}
              setSelectedRows={handleSetSelectedRows}
              expandedGroupIds={expandedGroupIds}
              onExpandedGroupIdsChange={(expanded) => setExpandedGroupIds(expanded)}
            />
          </$WrappingTable>
        </Grid>
      </Grid>
      <$ButtonContainer container justifyContent='flex-end'>
        <NewButton onClick={() => onSave([...selectedRows].filter((id) => id > 0))}>Save</NewButton>
      </$ButtonContainer>
    </$ModalContainer>
  )
}
